import React from "react";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ScrollReveal from "../components/site/ScrollReveal";
import {
  PlannerBlock, MelbourneSection, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { blogsForService } from "../data/internalLinks";

export default function BrandPhotography() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <WhyMatters />
      <CampaignSpread />
      <Categories />
      <Process />
      <PlannerBlock
        eyebrow="Plan it now · Indicative scope"
        headline="Scope your campaign"
        italic="before the budget meeting."
        blurb="Use the planner to scope dishes, drinks, venue moments and reels — we'll send back a structured campaign quote with deliverables, licences and timelines."
      />
      <MelbourneSection
        serviceLabel="Brand Photography"
        eyebrow="Melbourne hospitality · Campaign coverage"
        headline="A brand photographer for"
        italic="Melbourne openings."
        intro="Multi-frame campaigns for new restaurant openings, expansions, franchise rollouts, PR features and refreshes. Working with operators, agencies and designers across Melbourne — coordinated, on-brief, on-deadline."
      />
      <SisterServices exclude="brand" />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More on brand"
        italic="and campaign work."
        theme="ink"
        cols={3}
        items={blogsForService("brand-photography")}
      />
      <FAQSection
        eyebrow="Common questions · Brand photography Melbourne"
        headline="Scope, licensing,"
        italic="and rollout."
        intro="Brand and campaign shoots are quoted bespoke. The questions below cover the most common scope decisions — happy to talk through your specific brief on a call."
        faqs={BRAND_FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Campaign briefs"
        headline="Ready to launch"
        italic="something properly?"
        intro="Quick form below or call directly. For campaigns we'll typically run a 15-minute call to scope properly before sending a written quote and deliverables list."
        backdropSlug="hero-banquet-01"
        formTitle="Tell us about your campaign."
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="hero-banquet-01" alt="Brand photography Melbourne — hospitality campaign banquet" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/40 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Brand Photography Melbourne" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Brand Photography Melbourne · Campaigns · Openings · PR · Franchise
        </p>

        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}>
          Campaigns shot like
          <br />
          you <span className="italic font-light text-ember">mean it.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          Multi-frame brand photography for Melbourne hospitality — new
          restaurant openings, expansions, refreshes, franchise rollouts and
          PR features. Coordinated with your designer, sized for every
          channel, delivered as a structured asset library.
        </p>

        <p className="mt-7 max-w-xl">
          <span className="inline-block bg-ember text-bone font-bold text-sm md:text-base uppercase tracking-wider2 px-4 py-2.5">
            Campaign Quotes · Bespoke
          </span>
        </p>
        <p className="mt-3 text-xs text-bone/55 max-w-md leading-relaxed">
          One-day editorial campaigns from $1,800. Multi-day, multi-venue and
          franchise rollouts quoted per scope. Press kits and licensing
          included.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <a href="#enquiry" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">Request A Campaign Quote</CTAButton>
          </a>
          <a href="#planner" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">Scope The Brief</CTAButton>
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-12 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl border-t border-bone/15 pt-6">
        {[
          ["Format", "1–3 day production"],
          ["Turnaround", "7–10 days"],
          ["Licence", "Full · all channels"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider2 text-ember mb-1.5 font-bold">{k}</p>
            <p className="font-sans font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyMatters = () => (
  <section className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Why brand photography Melbourne operators commission
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            An opening only lands once.
            <br />
            <span className="italic font-light text-ember">Shoot it like it.</span>
          </h2>

          <div className="mt-12 grid gap-7 text-bone/85 leading-relaxed text-base md:text-lg">
            <ScrollReveal>
              <p>
                A new venue, an expansion, a rebrand, a franchise rollout —
                these are the moments where a strong image library does the
                heaviest lifting. PR, press kits, paid media, OOH, owner
                investor decks, all running off the same coordinated set.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <p>
                We treat a brand shoot like a campaign, not a portrait. A
                tight brief, a shot list, a styling plan, locked deliverables
                and a clean licence. <strong className="text-bone">Designers and
                agencies get usable hi-res files,</strong> not 1,000 RAW frames
                to wade through.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p>
                Format-flexible: single venue, multi-venue, multi-day,
                franchise rollouts of 10+ locations on a repeatable brief.
                Everything coordinated by us — you brief once, we deliver
                everything.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={280}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-4">
                {[
                  "Opening & launch campaigns",
                  "Press kits & PR features",
                  "Franchise rollouts",
                  "Multi-venue groups",
                  "Lifestyle & people imagery",
                  "Coordinated with your designer",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check size={16} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-3 md:space-y-5 lg:mt-4">
          <Photo slug="hero-banquet-01" alt="Campaign brand photography Melbourne" ratio="4/5" sizes="(min-width: 1024px) 35vw, 100vw" />
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            <Photo slug="hero-spread-01" alt="Restaurant launch photography Melbourne" ratio="1/1" sizes="20vw" />
            <Photo slug="menu-malai-tikka" alt="Hero campaign frame Melbourne" ratio="1/1" sizes="20vw" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CampaignSpread = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-6">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Editorial · One campaign, one palette
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Twenty frames.
            <br />
            <span className="italic font-light text-ember">One conversation.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            A good campaign reads as one idea across every frame — colour,
            light, gesture, plating. Designers can drop any image into any
            layout and the brand stays whole. We shoot in palettes, not in
            individual photographs.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 md:gap-5">
        <div className="col-span-12 md:col-span-7">
          <Photo slug="hero-banquet-01" alt="Editorial campaign photography Melbourne" ratio="16/10" sizes="(min-width: 768px) 55vw, 100vw" />
        </div>
        <div className="col-span-12 md:col-span-5 grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-5">
          <Photo slug="hero-spread-01" alt="Brand spread Melbourne" ratio="1/1" sizes="20vw" />
          <Photo slug="menu-malai-tikka" alt="Brand hero Melbourne" ratio="1/1" sizes="20vw" />
        </div>
      </div>
    </div>
  </section>
);

const CATEGORIES = [
  { n: "01", title: "Opening campaigns", slug: "hero-banquet-01", desc: "Multi-frame launch sets for new restaurants — hero plates, venue, lifestyle, PR portrait. One shoot, one launch." },
  { n: "02", title: "Press kit", slug: "hero-spread-01", desc: "Structured asset library sized for press — hero, secondary, environmental, owner portrait, fact-sheet imagery." },
  { n: "03", title: "Franchise rollout", slug: "menu-butter-chicken", desc: "Repeatable brief across 5, 10, 50 venues. Consistent palette, lighting and crop spec across every location." },
  { n: "04", title: "Refresh & rebrand", slug: "menu-malai-tikka", desc: "Re-shoot the existing menu, room and lifestyle library when the brand evolves — no AI fillers, no stock substitutions." },
  { n: "05", title: "Lifestyle & people", slug: "hero-banquet-01", desc: "Owner portraits, chef-at-pass, service moments. Honest, editorial — no agency models, no awkward smiles." },
  { n: "06", title: "Paid & OOH", slug: "hero-spread-01", desc: "Hero frames built for paid social, OOH, transit and outdoor. Square, vertical, landscape — every crop, every channel." },
  { n: "07", title: "Multi-venue groups", slug: "venue-buffet-01", desc: "Hospitality groups with multiple venues — coordinated shoots, locked palette, one library across all locations." },
  { n: "08", title: "Annual content", slug: "menu-falooda", desc: "Quarterly campaign retainers for venues running constant content. Plan it once, shoot it once a quarter." },
];

const Categories = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            What we photograph · Brand & campaign
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Campaigns for every
            <br />
            <span className="italic font-light text-ember">scale of brief.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            One venue or twenty. A new opening or a quiet refresh. Briefs
            scoped together with you and your designer — delivered as a
            structured, channel-ready library.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {CATEGORIES.map((c) => (
          <article key={c.n} className="bg-ink hover:bg-[#161613] transition-colors p-5 md:p-6 flex flex-col">
            <Photo slug={c.slug} alt={`${c.title} — Brand photography Melbourne`} ratio="4/5" sizes="(min-width: 1024px) 22vw, 50vw" />
            <div className="mt-5 flex flex-col flex-1">
              <span className="font-display italic text-2xl text-ember leading-none">{c.n}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-2.5">{c.title}</h3>
              <p className="mt-3 text-sm text-bone/70 leading-relaxed flex-1">{c.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const STEPS = [
  { n: "01", title: "Brief & scope", desc: "30-minute call to map the campaign — channels, deliverables, deadlines and any designer/agency in the loop." },
  { n: "02", title: "Shot list & styling", desc: "We send a structured shot list and palette before the shoot. You approve, we lock it. No surprises on the day." },
  { n: "03", title: "Production day", desc: "On-location, on-brief. We shoot to the list — hero frames first, environmental and supporting frames next, B-roll if needed." },
  { n: "04", title: "Edit & delivery", desc: "Edited master files plus channel crops in 7–10 business days. Delivered in a single shareable folder, named to your spec." },
];

const Process = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            How a brand shoot runs · From brief to delivery
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Four steps,
            <br />
            <span className="italic font-light text-ember">zero guesswork.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            Brand work runs on locked scope and clear deliverables. We do the
            project management — owners and designers just brief once and
            review the final library.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
        {STEPS.map((s) => (
          <div key={s.n} className="bg-bone p-7 md:p-8">
            <span className="font-display italic text-2xl text-ember leading-none">{s.n}</span>
            <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-3">{s.title}</h3>
            <p className="mt-4 text-sm text-char leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const BRAND_FAQS = [
  { q: "How is a brand shoot different from a menu or food shoot?", a: "Menu and food shoots produce useful single images on a per-dish basis. A brand shoot produces a coordinated library — hero plates, environmental, lifestyle, people and detail frames, all sharing one palette and consistent lighting. You can drop any of them into any layout and the brand reads as one piece." },
  { q: "Do you work with our designer or agency directly?", a: "Yes — most campaigns run with a designer or in-house marketing lead in the loop. We send shot lists, mood references and a final palette before the shoot, and we deliver in any folder structure or naming convention your designer prefers." },
  { q: "What does the licence cover?", a: "Full owned-and-earned channel licence — paid social, OOH, transit, print, in-venue, website, PR, EDM, packaging. Multi-venue groups and franchise rollouts get a group licence covering every location. Nothing extra to pay for usage." },
  { q: "Can you shoot a franchise rollout across multiple cities?", a: "Yes. We've structured rollouts as a repeatable production kit — same shot list, same lighting kit, same delivery spec at every venue. Travel costs estimated upfront. Strongest network in Melbourne; we can travel to Sydney, Brisbane, Adelaide and regional Victoria on quote." },
  { q: "What turnaround on a launch campaign?", a: "Standard is 7–10 business days for full delivery. Launch-day or PR-deadline turnarounds (3–5 days) are available — let us know on the call, we'll lock it in writing." },
  { q: "Do you do video and reels as part of brand shoots?", a: "Yes — short-form video and reels can be bolted onto a brand day. We typically deliver 3–5 short-form vertical clips per shoot day, edited and music-cleared for paid and organic social." },
];
